import React from "react";
import { siteImages } from "../data/redlandGolfContent";
import { ButtonLink } from "../components/ButtonLink";
import { SiteImage } from "../components/SiteImage";
import { useSiteContent } from "../context/ContentContext";
import {
  Card,
  EditorialCard,
  FeatureGrid,
  HomeHero,
  PhotoPanel,
  QuickActionCard,
  SectionHeading,
} from "../components/RedlandComponents";

const EVENT_FALLBACK = [siteImages.golfDetail, siteImages.rangeWide, siteImages.palmCourse];

export default function Home() {
  const { announcements, homepage: hp, rates, memberships, homeEvents, about, contact, preview } =
    useSiteContent();

  return (
    <>
      {preview ? (
        <div className="preview-ribbon" data-testid="preview-ribbon">
          Preview mode — showing unpublished draft changes. This is only visible to you.
        </div>
      ) : null}

      {announcements.enabled && announcements.items.length > 0 ? (
        <div className="announcement-bar" data-testid="announcement-bar">
          <div className="container">
            {announcements.items.map((a, i) => (
              <span key={a.id || i}>{a.text}</span>
            ))}
          </div>
        </div>
      ) : null}

      {hp.hero.visible !== false ? (
        <HomeHero
          eyebrow={hp.hero.eyebrow}
          title={hp.hero.title}
          copy={hp.hero.copy}
          image={hp.hero.image}
          facts={hp.hero.facts || []}
          actions={
            <>
              {(hp.hero.buttons || []).map((b, i) => (
                <ButtonLink key={i} href={b.href} variant={b.variant || "primary"}>
                  {b.label}
                </ButtonLink>
              ))}
            </>
          }
        />
      ) : null}

      {hp.quickActions.visible !== false && (hp.quickActions.items || []).length > 0 ? (
        <section className="quick-actions-section" aria-label="Quick actions">
          <div className="container quick-action-grid">
            {hp.quickActions.items.map((action, index) => (
              <QuickActionCard key={action.label || index} {...action} index={index} />
            ))}
          </div>
        </section>
      ) : null}

      {hp.video.visible !== false && hp.video.embedUrl ? (
        <section className="section video-section">
          <div className="container">
            <div className="video-feature">
              <div className="video-copy">
                <p className="eyebrow">{hp.video.eyebrow}</p>
                <h2>{hp.video.title}</h2>
                <p>{hp.video.copy}</p>
              </div>
              <div className="video-frame">
                <iframe
                  src={hp.video.embedUrl}
                  title={hp.video.title}
                  allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="strict-origin-when-cross-origin"
                />
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {hp.about.visible !== false ? (
        <section className="section editorial-section">
          <div className="container editorial-grid">
            <PhotoPanel image={hp.about.image} label={hp.about.photoLabel} className="about-photo" />
            <div className="editorial-copy">
              <SectionHeading eyebrow={hp.about.eyebrow} title={hp.about.title} copy={hp.about.copy} />
              <FeatureGrid items={(about.features || []).slice(0, 4)} />
              {hp.about.buttonLabel ? (
                <ButtonLink href={hp.about.buttonHref} variant="secondary">
                  {hp.about.buttonLabel}
                </ButtonLink>
              ) : null}
            </div>
          </div>
        </section>
      ) : null}

      {hp.rates.visible !== false ? (
        <section className="section rates-home">
          <div className="container">
            <SectionHeading eyebrow={hp.rates.eyebrow} title={hp.rates.title} copy={hp.rates.copy} />
            <div className="rates-preview">
              <Card className="rate-preview-card">
                <p className="eyebrow">Weekday Morning</p>
                <h3>{rates.weekday?.[0]?.price}</h3>
                <p>{rates.weekday?.[0]?.label}</p>
              </Card>
              <Card className="rate-preview-card is-featured">
                <p className="eyebrow">Weekend Morning</p>
                <h3>{rates.weekend?.[0]?.price}</h3>
                <p>{rates.weekend?.[0]?.label}</p>
              </Card>
              <Card className="rate-preview-card">
                <p className="eyebrow">Twilight</p>
                <h3>{rates.weekday?.[2]?.price}</h3>
                <p>{rates.weekday?.[2]?.label}</p>
              </Card>
            </div>
            {hp.rates.buttonLabel ? (
              <div className="section-action">
                <ButtonLink href={hp.rates.buttonHref} variant="secondary">
                  {hp.rates.buttonLabel}
                </ButtonLink>
              </div>
            ) : null}
          </div>
        </section>
      ) : null}

      {hp.membership.visible !== false ? (
        <section className="section membership-home">
          <div className="container membership-feature">
            <div>
              <SectionHeading
                eyebrow={hp.membership.eyebrow}
                title={hp.membership.title}
                copy={hp.membership.copy}
              />
              <ul className="clean-list membership-benefit-list">
                {(memberships.benefits || []).slice(0, 5).map((benefit) => (
                  <li key={benefit}>{benefit}</li>
                ))}
              </ul>
              {hp.membership.buttonLabel ? (
                <ButtonLink href={hp.membership.buttonHref} variant="light">
                  {hp.membership.buttonLabel}
                </ButtonLink>
              ) : null}
            </div>
            <Card className="membership-preview-card">
              <SiteImage {...hp.membership.image} />
              <p className="eyebrow">Featured Plan</p>
              <h3>{memberships.pricing?.[0]?.name}</h3>
              <p>{memberships.pricing?.[0]?.monthly}</p>
              <strong>{memberships.pricing?.[0]?.yearly}</strong>
            </Card>
          </div>
        </section>
      ) : null}

      {hp.events.visible !== false && homeEvents.length > 0 ? (
        <section className="section events-home">
          <div className="container">
            <SectionHeading eyebrow={hp.events.eyebrow} title={hp.events.title} copy={hp.events.copy} />
            <div className="event-editorial-grid">
              {homeEvents.map((event, index) => (
                <EditorialCard
                  key={event.id || event.title}
                  eyebrow={event.date || "Activity"}
                  title={event.title}
                  copy={
                    event.description ||
                    "A regular club activity with the friendly pace and local feel Redland is known for."
                  }
                  image={event.image?.src ? event.image : EVENT_FALLBACK[index % EVENT_FALLBACK.length]}
                  href={event.link || "/golf-events"}
                  linkLabel="View Details"
                />
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {hp.junior.visible !== false ? (
        <section className="section junior-home">
          <div className="container junior-home-grid">
            <div className="junior-home-copy">
              <SectionHeading
                eyebrow={hp.junior.eyebrow}
                title={hp.junior.title}
                copy={hp.junior.copy}
              />
              <div className="inline-actions">
                {hp.junior.exploreLabel ? (
                  <ButtonLink href={hp.junior.exploreHref} variant="light">
                    {hp.junior.exploreLabel}
                  </ButtonLink>
                ) : null}
                {hp.junior.campLabel ? (
                  <ButtonLink href={hp.junior.campHref} variant="secondary">
                    {hp.junior.campLabel}
                  </ButtonLink>
                ) : null}
              </div>
            </div>
            <Card className="junior-program-card">
              <SiteImage {...hp.junior.image} />
              <h3>{hp.junior.cardTitle}</h3>
              <p>{hp.junior.cardLine1}</p>
              <p>{hp.junior.cardLine2}</p>
            </Card>
          </div>
        </section>
      ) : null}

      {hp.sandtrap.visible !== false ? (
        <section className="section sandtrap-home">
          <div className="container sandtrap-grid">
            <SiteImage {...hp.sandtrap.image} />
            <div>
              <SectionHeading
                eyebrow={hp.sandtrap.eyebrow}
                title={hp.sandtrap.title}
                copy={hp.sandtrap.copy}
              />
              {hp.sandtrap.buttonLabel ? (
                <ButtonLink href={hp.sandtrap.buttonHref} variant="secondary">
                  {hp.sandtrap.buttonLabel}
                </ButtonLink>
              ) : null}
            </div>
          </div>
        </section>
      ) : null}

      {hp.life.visible !== false && (hp.life.images || []).length > 0 ? (
        <section className="section life-section">
          <div className="container">
            <SectionHeading eyebrow={hp.life.eyebrow} title={hp.life.title} />
            <div className="life-strip">
              {hp.life.images.map((item, i) => (
                <figure key={item.label || i}>
                  <SiteImage src={item.src ?? ""} alt={item.alt} />
                  <figcaption>{item.label}</figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {hp.finalCta.visible !== false ? (
        <section className="final-home-cta">
          <div className="container">
            <div className="photo-cta">
              <SiteImage {...hp.finalCta.image} />
              <div>
                <p className="eyebrow">{hp.finalCta.eyebrow}</p>
                <h2>{hp.finalCta.title}</h2>
                {hp.finalCta.note ? <p>{hp.finalCta.note}</p> : null}
                <div className="cta-actions">
                  {(hp.finalCta.buttons || []).map((b, i) => (
                    <ButtonLink
                      key={i}
                      href={b.href === "CONTACT_PHONE" ? contact.phoneHref : b.href}
                      variant={b.variant || "secondary"}
                    >
                      {b.label}
                    </ButtonLink>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      ) : null}
    </>
  );
}
